// Owns registration and sign-in controls. The server owns credentials,
// age-eligibility policy, browser binding, and OAuth identity verification.
import { MINIMUM_AGE } from '../../../packages/domain/src/12-account-eligibility.mjs';
/** Create provider sign-in controls with stale-response protection; credentials stay server-owned. */
export function createAuth({
  api,
  e,
  append,
  button,
  setLocale,
  currentLocale = () => 'en',
  LOCALES = {},
  translate,
  publicLocale = () => 'en',
  rememberPublicLocale = () => {},
  requestTimeoutMs = 15000,
}) {
  let generation = 0;
  let registrationAvailable = false;
  let termsVersion;
  let registrationPending = false;
  let registrationBound = false;
  let loginBound = false;
  let loginPending = false;
  let providerPending = false;
  let providerAvailable = false;
  let providerStatusVisible = false;
  let step = 'method';
  let registerMode = true;
  let initialized;
  let languageGeneration = 0;
  /** Apply the accepted session through the existing shell state owner, then choose its destination. */
  function bindAccounts(ctx, onAccepted) {
    const accept = async (snapshot) => {
      ctx.state = snapshot;
      await ctx.setLocale(snapshot.settings.locale);
      ctx.experience =
        snapshot.settings.experience === 'circles'
          ? 'wall'
          : (snapshot.settings.experience ?? 'wall');
      ctx.feed.reset(ctx.mode);
      ctx.feedScroll = 0;
      renderCopy();
      onAccepted();
    };
    bindLogin(accept);
    bindRegistration(accept);
  }
  const text = (key, fallback, values = {}) => {
    const translated = translate?.(key, values);
    return (translated && translated !== key ? translated : fallback).replace(
      /\{(\w+)\}/g,
      (_, name) => String(values[name] ?? `{${name}}`),
    );
  };
  async function request(path, body) {
    const responsePath = ['/api/login', '/api/register', '/api/auth/enrollment/complete'].includes(
      path,
    )
      ? `${path}?projection=startup-v1`
      : path;
    const controller = new AbortController();
    const timeoutError = new Error(
      path === '/api/register'
        ? text(
            'auth.registrationTimeout',
            'This is taking too long. Your account may have been created; try signing in before trying again.',
          )
        : path === '/api/auth/enrollment/complete'
          ? text(
              'auth.enrollmentTimeout',
              'Your account may have been created. Continue with Google or sign in before trying to create it again.',
            )
          : text('auth.requestTimeout', 'This is taking too long. Please try again.'),
    );
    timeoutError.code = 'AUTH_REQUEST_TIMEOUT';
    let timer;
    const expired = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(timeoutError);
      }, requestTimeoutMs);
    });
    try {
      return await Promise.race([api(responsePath, body, { signal: controller.signal }), expired]);
    } catch (failure) {
      throw controller.signal.aborted ? timeoutError : failure;
    } finally {
      clearTimeout(timer);
    }
  }
  function renderCopy() {
    for (const node of document.querySelectorAll('[data-auth-copy]'))
      node.textContent = text(node.dataset.authCopy, node.textContent, {
        provider: node.dataset.provider,
        minimumAge: MINIMUM_AGE,
      });
    for (const node of document.querySelectorAll('[data-auth-placeholder]'))
      node.placeholder = text(node.dataset.authPlaceholder, node.placeholder);
    for (const node of document.querySelectorAll('[data-auth-alt]'))
      node.alt = text(node.dataset.authAlt, node.alt);
    document.querySelector('#auth-title').textContent = ['consent', 'provider-consent'].includes(
      step,
    )
      ? text('auth.finishTitle', 'Finish creating your account')
      : registerMode
        ? text('auth.createTitle', 'Join your people.')
        : text('auth.signInTitle', 'Welcome back.');
    document.querySelector('#auth-introduction').textContent = [
      'consent',
      'provider-consent',
    ].includes(step)
      ? text('auth.finishIntroduction', 'Confirm these two things before we create your account.')
      : registerMode
        ? text('auth.createIntroduction', 'Choose how you want to join.')
        : text('auth.signInIntroduction', 'Sign in with your email and password.');
    document.querySelector('#auth-consent-purpose').textContent = text(
      'auth.finishIntroduction',
      'Confirm these two things before we create your account.',
    );
    document.querySelector('#register-submit').textContent = text(
      step === 'email' ? 'auth.continue' : 'auth.createAccount',
      step === 'email' ? 'Continue' : 'Create account',
    );
    document.querySelector('#auth-back').textContent = text(
      step === 'provider-consent' ? 'auth.cancelSignup' : 'auth.back',
      step === 'provider-consent' ? 'Cancel signup' : 'Back',
    );
    for (const control of document.querySelector('#auth-languages').children)
      control.setAttribute('aria-pressed', String(control.dataset.locale === currentLocale()));
  }
  async function changeLanguage(locale) {
    const request = ++languageGeneration;
    const error = document.querySelector('#auth-language-error');
    error.textContent = '';
    try {
      await setLocale?.(locale);
      if (request !== languageGeneration) return;
      rememberPublicLocale(currentLocale());
      renderCopy();
    } catch {
      if (request !== languageGeneration) return;
      error.textContent = text(
        'auth.languageUnavailable',
        'This language could not load. Your form is unchanged; try again.',
      );
    }
  }
  async function initializeLanguage() {
    if (!initialized)
      initialized = (async () => {
        const host = document.querySelector('#auth-languages');
        for (const [locale, name] of Object.entries(LOCALES)) {
          const control = button(name, () => changeLanguage(locale), 'auth-language');
          control.dataset.locale = locale;
          control.setAttribute('aria-pressed', 'false');
          host.append(control);
        }
        await changeLanguage(publicLocale());
      })();
    await initialized;
  }
  /** Bind password sign-in once and leave accepted account state with the shell owner. */
  function bindLogin(onAuthenticated) {
    if (loginBound) return;
    loginBound = true;
    document.querySelector('#login-form').addEventListener('submit', async (event) => {
      event.preventDefault();
      if (loginPending || registrationPending || providerPending) return;
      const submit = event.target.querySelector('button');
      const password = document.querySelector('#password');
      const error = document.querySelector('#login-error');
      loginPending = true;
      submit.disabled = true;
      error.textContent = '';
      try {
        const snapshot = await request('/api/login', {
          email: document.querySelector('#email').value,
          password: password.value,
        });
        password.value = '';
        await onAuthenticated(snapshot);
      } catch (failure) {
        error.textContent = failure.message;
      } finally {
        loginPending = false;
        submit.disabled = false;
      }
    });
  }
  function displayStep(next, focus = true) {
    step = next;
    const consent = ['consent', 'provider-consent'].includes(step);
    const email = step === 'email' || step === 'consent';
    registerMode = step !== 'login';
    document.querySelector('#login-form').hidden = step !== 'login';
    document.querySelector('#register-form').hidden = !(email || consent);
    document.querySelector('#register-details').hidden = !email || consent;
    for (const id of ['register-name', 'register-email', 'register-password'])
      document.querySelector('#' + id).disabled = !email || consent;
    document.querySelector('#register-submit').hidden = !(email || consent);
    document.querySelector('#auth-consent').hidden = !consent;
    const policyLinks = document.querySelector('#auth-policy-links');
    if (policyLinks) policyLinks.hidden = consent;
    document.querySelector('#auth-enrollment-identity').hidden = step !== 'provider-consent';
    for (const id of ['register-age', 'register-terms'])
      document.querySelector('#' + id).disabled = !consent;
    document.querySelector('#auth-back').hidden = step === 'method';
    document.querySelector('#provider-signin').hidden =
      (!providerAvailable && !providerStatusVisible) || (step !== 'method' && step !== 'login');
    document.querySelector('#auth-methods').hidden = consent || !registrationAvailable;
    document.querySelector('#auth-signin').setAttribute('aria-pressed', String(step === 'login'));
    document.querySelector('#auth-register').setAttribute('aria-pressed', String(email));
    renderCopy();
    if (focus)
      document
        .querySelector(
          consent
            ? '#register-age'
            : step === 'login'
              ? '#email'
              : step === 'email'
                ? '#register-name'
                : '#auth-register',
        )
        .focus();
  }
  function chooseMethod(register, focus = true) {
    if (
      registrationPending ||
      loginPending ||
      providerPending ||
      (register && !registrationAvailable)
    )
      return;
    if (!register) document.querySelector('#register-password').value = '';
    for (const id of ['register-age', 'register-terms'])
      document.querySelector('#' + id).checked = false;
    displayStep(register ? 'email' : 'login', focus);
  }
  /** Bind registration once; only an accepted server snapshot may enter the signed-in app. */
  function bindRegistration(onAuthenticated) {
    if (registrationBound) return;
    registrationBound = true;
    document.querySelector('#auth-signin').addEventListener('click', () => chooseMethod(false));
    document.querySelector('#auth-register').addEventListener('click', () => chooseMethod(true));
    document.querySelector('#auth-back').addEventListener('click', async () => {
      if (registrationPending || loginPending || providerPending) return;
      if (step === 'provider-consent') {
        const current = generation;
        const controls = [
          document.querySelector('#auth-back'),
          document.querySelector('#register-submit'),
        ];
        registrationPending = true;
        controls.forEach((control) => {
          control.disabled = true;
        });
        try {
          await request('/api/auth/enrollment/cancel', {});
          if (current !== generation) return;
          globalThis.history?.replaceState(null, '', '/#/feed');
        } catch (failure) {
          if (current === generation)
            document.querySelector('#register-error').textContent = failure.message;
          return;
        } finally {
          registrationPending = false;
          controls.forEach((control) => {
            control.disabled = false;
          });
        }
      }
      displayStep(step === 'consent' ? 'email' : 'method');
    });
    const form = document.querySelector('#register-form');
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (
        !registrationAvailable ||
        registrationPending ||
        loginPending ||
        providerPending ||
        !form.reportValidity()
      )
        return;
      if (step === 'email') {
        displayStep('consent');
        return;
      }
      if (!['consent', 'provider-consent'].includes(step)) return;
      if (!document.querySelector('#register-terms').checked) {
        document.querySelector('#register-error').textContent = text(
          'auth.acceptTerms',
          'Agree to the Terms before creating an account.',
        );
        document.querySelector('#register-terms').focus();
        return;
      }
      const password = document.querySelector('#register-password');
      const error = document.querySelector('#register-error');
      const controls = [
        document.querySelector('#auth-back'),
        form.querySelector('button') ?? document.querySelector('#register-submit'),
        document.querySelector('#register-submit'),
        document.querySelector('#auth-signin'),
        document.querySelector('#auth-register'),
      ];
      registrationPending = true;
      controls.forEach((control) => {
        control.disabled = true;
      });
      form.setAttribute('aria-busy', 'true');
      error.textContent = '';
      const providerCompletion = step === 'provider-consent';
      let accepted = false;
      let failureTimedOut = false;
      try {
        const snapshot =
          step === 'provider-consent'
            ? await request('/api/auth/enrollment/complete', {
                ageEligible: document.querySelector('#register-age').checked,
                termsAccepted: document.querySelector('#register-terms').checked,
                termsVersion,
                locale: currentLocale(),
              })
            : await request('/api/register', {
                name: document.querySelector('#register-name').value,
                email: document.querySelector('#register-email').value,
                password: password.value,
                ageEligible: document.querySelector('#register-age').checked,
                termsVersion,
                locale: currentLocale(),
              });
        accepted = true;
        await onAuthenticated(snapshot);
      } catch (failure) {
        failureTimedOut = failure.code === 'AUTH_REQUEST_TIMEOUT';
        error.textContent = accepted
          ? text(
              'auth.acceptedViewFailure',
              'Your account was created, but this view could not open. Sign in to continue.',
            )
          : failure.message;
      } finally {
        if (accepted || failureTimedOut) password.value = '';
        registrationPending = false;
        form.setAttribute('aria-busy', 'false');
        controls.forEach((control) => {
          control.disabled = false;
        });
        if (failureTimedOut && providerCompletion) {
          chooseMethod(false);
          document.querySelector('#login-error').textContent = error.textContent;
        }
        if (accepted) {
          document.querySelector('#register-name').value = '';
          document.querySelector('#register-email').value = '';
          document.querySelector('#register-age').checked = false;
          document.querySelector('#register-terms').checked = false;
          chooseMethod(false, !!error.textContent);
          if (error.textContent)
            document.querySelector('#login-error').textContent = error.textContent;
        }
      }
    });
  }
  let disposeProviderControls = () => {};
  async function renderProviders() {
    const current = ++generation;
    disposeProviderControls();
    await initializeLanguage();
    if (current !== generation) return;
    const host = document.querySelector('#provider-signin');
    const status = e('p', 'subtle', text('auth.checkingProviders', 'Checking sign-in options…'));
    status.setAttribute('role', 'status');
    const error = e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const separator = e('p', 'auth-or', text('auth.or', 'or'));
    separator.dataset.authCopy = 'auth.or';
    providerAvailable = false;
    providerStatusVisible = true;
    separator.hidden = true;
    host.replaceChildren(separator, status, error);
    host.hidden = step !== 'method' && step !== 'login';
    const controls = [];
    let pending = false;
    const update = () =>
      controls.forEach((control) => {
        control.disabled = pending || loginPending || registrationPending;
      });
    disposeProviderControls = () => {};
    try {
      const providers = await request('/api/auth/providers');
      if (current !== generation) return;
      termsVersion =
        typeof providers.termsVersion === 'string' ? providers.termsVersion : undefined;
      registrationAvailable = providers.registration === true && !!termsVersion;
      providerAvailable = providers.google === true || providers.apple === true;
      providerStatusVisible = false;
      separator.hidden = !providerAvailable;
      document.querySelector('#auth-methods').hidden = !registrationAvailable;
      document.querySelector('#local-demo-note').hidden = providers.beta === true;
      document.querySelector('#auth-environment').dataset.authCopy =
        providers.beta === true ? 'auth.betaNotice' : 'auth.localNotice';
      document.querySelector('#register-submit').disabled = !registrationAvailable;
      displayStep(registrationAvailable ? (step === 'method' ? 'method' : step) : 'login', false);
      status.textContent = termsVersion
        ? ''
        : text(
            'auth.policyUnavailable',
            'Sign-in policy information is unavailable. Try again later.',
          );
      for (const [provider, label] of [
        ['google', 'Google'],
        ['apple', 'Apple'],
      ]) {
        if (providers[provider] !== true) continue;
        const control = button(
          text('auth.continueWith', 'Continue with {provider}', {
            provider: label,
          }),
          async () => {
            if (pending || loginPending || registrationPending) return;
            const startedStep = step;
            const startedHash = globalThis.location?.hash;
            document.querySelector('#register-password').value = '';
            pending = true;
            providerPending = true;
            error.textContent = '';
            update();
            try {
              const result = await request('/api/auth/start', {
                provider,
                locale: currentLocale(),
              });
              if (
                current !== generation ||
                step !== startedStep ||
                globalThis.location?.hash !== startedHash
              )
                return;
              const redirect = new URL(result.url);
              if (redirect.protocol !== 'https:')
                throw new Error(
                  text('auth.invalidProvider', 'The provider returned an invalid sign-in URL.'),
                );
              window.location.assign(redirect.href);
            } catch (failure) {
              error.textContent = failure.message;
            } finally {
              pending = false;
              providerPending = false;
              setTimeout(update, 0);
            }
          },
          'secondary-button provider-button',
        );
        const copy = e(
          'span',
          '',
          text('auth.continueWith', 'Continue with {provider}', {
            provider: label,
          }),
        );
        copy.dataset.authCopy = 'auth.continueWith';
        copy.dataset.provider = label;
        if (provider === 'google') {
          const mark = e('img', 'google-signin-mark');
          mark.src = '/assets/google-g.png';
          mark.alt = '';
          mark.width = 20;
          mark.height = 20;
          control.replaceChildren(mark, copy);
        } else control.replaceChildren(copy);
        controls.push(control);
        host.append(control);
      }
      if (globalThis.location?.hash === '#/finish-signup') {
        try {
          const enrollment = await request('/api/auth/enrollment');
          if (current !== generation || globalThis.location?.hash !== '#/finish-signup') return;
          termsVersion = enrollment.termsVersion;
          registrationAvailable = enrollment.registration === true;
          document.querySelector('#auth-enrollment-identity').textContent =
            `${enrollment.name} · ${enrollment.email}`;
          displayStep('provider-consent', false);
          document.querySelector('#register-submit').disabled = !registrationAvailable;
          document.querySelector('#register-age').checked = false;
          document.querySelector('#register-terms').checked = false;
        } catch (failure) {
          if (current !== generation || globalThis.location?.hash !== '#/finish-signup') return;
          error.textContent = failure.message;
          displayStep('method', false);
        }
      }
      update();
      renderCopy();
    } catch (failure) {
      if (current !== generation) return;
      providerAvailable = false;
      providerStatusVisible = true;
      separator.hidden = true;
      displayStep('login', false);
      status.textContent = text('auth.providerUnavailable', 'Sign-in options could not load.');
      host.append(button(text('auth.retryProviders', 'Try again'), renderProviders));
      error.textContent = failure.message;
    }
  }
  return {
    renderProviders,
    bindRegistration,
    bindLogin,
    bindAccounts,
    renderCopy,
    changeLanguage,
  };
}

/** Canonical anonymous shell composition, shared by normal and invited sign-in. */
export function renderLoginShell(ctx, searchToggle) {
  document.querySelector('#login').hidden = false;
  document.querySelector('#app').hidden = true;
  searchToggle.hidden = true;
  document.querySelector('#top-preference-slot').hidden = true;
  document.querySelector('#top-mode-slot').hidden = true;
  document.querySelector('#global-search').hidden = true;
  document.querySelector('#mobile-nav').hidden = true;
  document
    .querySelector('#header-actions')
    .replaceChildren(
      ctx.e('span', 'demo-badge', location.protocol === 'https:' ? 'PUBLIC BETA' : 'LOCAL DEMO'),
      searchToggle,
    );
}
